import type { SQL } from 'drizzle-orm'
import type { PgColumn, PgTable } from 'drizzle-orm/pg-core'
import { and, asc, count, desc, eq, exists, gt, gte, inArray, lte, not, notInArray, or, sql } from 'drizzle-orm'
import { alias } from 'drizzle-orm/pg-core'

import type { Database } from '../client'
import {
  account,
  Achievement,
  AchievementProgress,
  Book,
  BookCollection,
  BookCollectionView,
  BookSearch,
  ContentShare,
  ContentShareRecipient,
  ContinueLesson,
  ContinueReading,
  Course,
  EarnedXp,
  EarnedXpDaily,
  Funder,
  HomeConnectRequest,
  InClassAttachment,
  invitation,
  LeaderboardEntry,
  LearnEnrollment,
  LearnProgress,
  LessonEnrollmentMetrics,
  LessonHistoryDaily,
  LessonMetricsDaily,
  LessonModule,
  LessonModuleSearch,
  LessonModuleView,
  LessonSubjectMetricsDaily,
  LoveBook,
  LoveBookCollection,
  LovedLessonModule,
  member,
  MyBookCollection,
  MyBookCollectionOrder,
  Notification,
  NotificationEvent,
  NotificationPreference,
  organization,
  PageReadingActivity,
  Premium,
  Price,
  Program,
  PushSubscription,
  QuizAttempt,
  ReadingHistoryDaily,
  ReadingJournal,
  ReadingMetricsDaily,
  ReadingSession,
  ReadingSessionSummary,
  SeatEntitlement,
  SentBookCollection,
  SentBookCollectionToUser,
  session,
  teamMember,
  TopAchievers,
  UnifiedSearch,
  user,
  UserMetadata,
  Voucher,
} from '../schema'

/**
 * Merging two child accounts into one.
 *
 * The same human can exist twice — created once by their school (S) and once by their parent (F) —
 * and Sambung Sekolah-Rumah lets the parent say the two are the same child. There is no Child table:
 * a child is a plain `user` row and the only thing tying it to a school or a family is a `member` row,
 * so a merge is a repoint of every row hanging off `user.id` from S to F, followed by the deletion of S.
 *
 * Read this before touching anything below. There is no audit log, no soft delete and no undo anywhere
 * in this schema. 48 of the 62 foreign keys onto `user.id` are ON DELETE CASCADE, so a row that has
 * not been repointed by the time S is deleted is destroyed, not orphaned — and the other 14 are SET
 * NULL, which anonymises the row silently instead of erroring. A partial merge is therefore
 * unrecoverable, which is why `mergeChildAccounts` runs inside ONE transaction the caller opens and
 * deletes S as its final statement. Take a database snapshot before running it against production.
 */

/**
 * The handle a caller holds inside `dbPool.transaction(...)`. Extracted via `Parameters<>` off
 * `Database['transaction']` — the same construction as `DatabaseOrTransaction` in
 * `@package/notification` — because `PgTransaction` is not assignable to `Database`, and a hand-rolled
 * `PgTransaction<...>` union would drift from however `Database` is defined.
 *
 * Taking a transaction rather than a `Database` is the point, not a convenience. `Database` is a union
 * of the pooled and the neon-http handles; both declare `.transaction`, but the neon-http one throws at
 * runtime, and locally BOTH handles are node-postgres, so a merge that opened its own transaction on a
 * `Database` would pass every local test and fail only in staging and production.
 */
type TransactionCallback = Parameters<Database['transaction']>[0]
export type DatabaseTransaction = Parameters<TransactionCallback>[0]

/** Read-only helpers shared by the merge and its preview accept either handle. */
type Queryable = Database | DatabaseTransaction

export interface MergeChildAccountsInput {
  /** F — the family's child row. Survives. */
  survivorUserId: string
  /** S — the school's student row. Deleted. */
  absorbedUserId: string
}

export interface MergeChildAccountsResult {
  survivorUserId: string
  absorbedUserId: string
  /** table name -> rows repointed, for the audit line and for tests. */
  movedRowCounts: Record<string, number>
  /**
   * table name -> rows deleted (derived tables and deduped duplicates).
   *
   * Two tables never lose a row and are counted here by their nearest equivalent: `premium` counts
   * seats released (`childId` set to NULL) beyond `releasedSchoolPremiumId` — the absorbed account's
   * seats returned to their pool, plus, in the one case where the survivor holds nothing live, the
   * survivor's expired seats detached to let a school seat land — and `home_connect_request` counts
   * the survivor's live request cancelled to make room for the absorbed account's.
   */
  deletedRowCounts: Record<string, number>
  /**
   * The absorbed account's live school seat freed by the seat rule, if any — never a seat of the
   * survivor's, which the rule never touches. Null whenever the rule did not fire, which is not the
   * same as "no school seat was freed": with no family coverage to defend the school's seat usually
   * lands, but one that collides with a survivor row the merge kept is still swept, unnamed, by
   * step 4's final statement.
   *
   * The absorbed account may hold more than one live seat (a current one and a prepaid next term);
   * every one of them is released and this names the later-ending of them, so it is not necessarily
   * the only seat the school got back. Seats that merely fail the overlap test are counted in
   * `deletedRowCounts`, not named here. `Premium.organizationId` decides which pool a released seat
   * lands in — the school's for a school seat, which is the school's to reassign and not the
   * parent's, though nothing forbids the absorbed account from having held a seat of some other
   * organization's.
   */
  releasedSchoolPremiumId: string | null
  /** Organization ids the survivor now belongs to. */
  organizationIds: string[]
  /** Ids whose lookup tables the caller must recompute. */
  rebuildUserIds: string[]
}

export interface MergePreview {
  survivorUserId: string
  absorbedUserId: string
  /** table name -> rows that WOULD move. */
  rowCounts: Record<string, number>
  /** Human-readable warnings: seat to be released, duplicate enrollments, etc. */
  warnings: string[]
  blockers: string[]
}

/** SQL predicate for "this organization is a family". `organization.type` is a text array. */
const IS_FAMILY_ORG = sql`'family' = ANY(${organization.type})`
/** SQL predicate for "this organization is a school". */
const IS_SCHOOL_ORG = sql`'school' = ANY(${organization.type})`

interface UserColumnRef {
  /** Table name as it appears in the result counts. */
  table: string
  target: PgTable
  column: PgColumn
  /** The TypeScript key of `column`, for `.set()`. Differs from the SQL name on the Better Auth tables. */
  key: string
}

function ref(table: string, target: PgTable, column: PgColumn, key: string): UserColumnRef {
  return { table, target, column, key }
}

/**
 * Columns repointed with a plain UPDATE. None of them sits in a user-scoped unique constraint, so two
 * rows for the same day, book or lesson are legitimate — the child really did read that book twice, in
 * two classrooms — and nothing can collide.
 */
const LEARNER_COLUMNS: UserColumnRef[] = [
  ref('learn_enrollment', LearnEnrollment, LearnEnrollment.userId, 'userId'),
  // Its unique is (lessonId, learnEnrollmentId); userId is not in it.
  ref('learn_progress', LearnProgress, LearnProgress.userId, 'userId'),
  ref('quiz_attempt', QuizAttempt, QuizAttempt.userId, 'userId'),
  // An append-only ledger: keeping every row IS the sum the parent expects. The one exception —
  // a second bonus row for a badge both accounts earned — is removed before this move runs.
  ref('earned_xp', EarnedXp, EarnedXp.userId, 'userId'),
  ref('in_class_attachment', InClassAttachment, InClassAttachment.userId, 'userId'),
  ref('reading_session', ReadingSession, ReadingSession.userId, 'userId'),
  ref('reading_session_summary', ReadingSessionSummary, ReadingSessionSummary.userId, 'userId'),
  ref('page_reading_activity', PageReadingActivity, PageReadingActivity.userId, 'userId'),
  // Whole collections move; two same-named collections on F are legal (there is no unique on name).
  // Folding one into the other would hand every existing share recipient of the surviving collection
  // silent read access to the folded-in books — `canAccessSharedEntity` grants by collection id with
  // no per-book scoping.
  ref('my_book_collection', MyBookCollection, MyBookCollection.userId, 'userId'),
  ref('sent_book_collection', SentBookCollection, SentBookCollection.userId, 'userId'),
  ref('book_search', BookSearch, BookSearch.userId, 'userId'),
  ref('lesson_module_search', LessonModuleSearch, LessonModuleSearch.userId, 'userId'),
  ref('unified_search', UnifiedSearch, UnifiedSearch.userId, 'userId'),
  ref('book_collection_view', BookCollectionView, BookCollectionView.userId, 'userId'),
  ref('lesson_module_view', LessonModuleView, LessonModuleView.userId, 'userId'),
  ref('content_share', ContentShare, ContentShare.actorId, 'actorId'),
  ref('notification_event', NotificationEvent, NotificationEvent.actorId, 'actorId'),
]

/**
 * Columns a child can never legitimately occupy — teachers grade, adults invite, admins create prices.
 * They are repointed anyway rather than left to the cascade, because the cascade would take other
 * people's data with it: `course.userId` cascades into every enrolment and progress row of every OTHER
 * student in the course, and `learn_progress.gradedByUserId` cascades into other pupils' progress.
 * Moving a stray row is harmless; cascading it is not. Expected to count zero.
 */
const ADULT_COLUMNS: UserColumnRef[] = [
  ref('course', Course, Course.userId, 'userId'),
  ref('course', Course, Course.originalUserId, 'originalUserId'),
  ref('learn_progress', LearnProgress, LearnProgress.gradedByUserId, 'gradedByUserId'),
  ref('invitation', invitation, invitation.inviterId, 'inviterId'),
  ref('price', Price, Price.createdByUserId, 'createdByUserId'),
  ref('voucher', Voucher, Voucher.createdByUserId, 'createdByUserId'),
  ref('funder', Funder, Funder.createdBy, 'createdBy'),
  ref('program', Program, Program.createdBy, 'createdBy'),
  ref('seat_entitlement', SeatEntitlement, SeatEntitlement.createdBy, 'createdBy'),
  ref('home_connect_request', HomeConnectRequest, HomeConnectRequest.parentUserId, 'parentUserId'),
  ref('home_connect_request', HomeConnectRequest, HomeConnectRequest.requestedBy, 'requestedBy'),
]

/**
 * Rows deleted outright; F's version wins.
 *
 * `session`: the device signed in as S must re-authenticate as F rather than inherit the family account
 * via a school login. `push_subscription` is a browser, not a person: S's is a shared school tablet, and
 * its `endpoint` is globally unique so a move would not collide — which is exactly why moving it would
 * be silent, and would deliver the family's home notifications to whichever child picks that device up
 * next. `notification_preference` collides on (user, category, channel) and the family's settings, or
 * their defaults, are what the parent chose. `achievement_progress` has no unique on (user, condition),
 * so duplicates do not error, they rot — both reader and writer collapse rows into a Map by condition.
 * F's own progress rows are left alone: `achievementBadge` overwrites `currentValue` absolutely for
 * every badge F does not yet hold, and never revisits one it does.
 *
 * `account` and the four `oauth_*` tables are left to the cascade: children never get a credential row
 * (`signUpChild` creates the user and stops). The preview warns if one exists anyway.
 */
const DROPPED_COLUMNS: UserColumnRef[] = [
  ref('session', session, session.userId, 'userId'),
  ref('push_subscription', PushSubscription, PushSubscription.userId, 'userId'),
  ref('notification_preference', NotificationPreference, NotificationPreference.userId, 'userId'),
  ref('achievement_progress', AchievementProgress, AchievementProgress.userId, 'userId'),
]

/**
 * Derived lookup tables. Deleted for BOTH ids and rebuilt by the caller, never merged: every one is a
 * function of the source rows (`compute<Metric>` in packages/trigger/src/metrics/), and a hand-merge of
 * ratios, distinct counts and percentage distributions is wrong in ways nothing later notices.
 * `top_achievers` cannot be merged at all (UNIQUE on `userId` alone) and its nightly cron rebuilds it
 * in full. `continue_reading`, `continue_lesson` and `leaderboard_entry` are no longer read (all three
 * are computed live); their rows are deleted only so nothing stale survives.
 *
 * Until the rebuild runs, the survivor's dashboards show only today (computed live); the rebuild
 * (`core / rollup-users` over all history) replaces every closed day in one pass.
 */
const DERIVED_COLUMNS: UserColumnRef[] = [
  ref('continue_reading', ContinueReading, ContinueReading.userId, 'userId'),
  ref('reading_journal', ReadingJournal, ReadingJournal.userId, 'userId'),
  ref('reading_metrics_daily', ReadingMetricsDaily, ReadingMetricsDaily.userId, 'userId'),
  ref('reading_history_daily', ReadingHistoryDaily, ReadingHistoryDaily.userId, 'userId'),
  ref('earned_xp_daily', EarnedXpDaily, EarnedXpDaily.userId, 'userId'),
  ref('lesson_metrics_daily', LessonMetricsDaily, LessonMetricsDaily.userId, 'userId'),
  ref('lesson_history_daily', LessonHistoryDaily, LessonHistoryDaily.userId, 'userId'),
  ref('lesson_subject_metrics_daily', LessonSubjectMetricsDaily, LessonSubjectMetricsDaily.userId, 'userId'),
  ref('lesson_enrollment_metrics', LessonEnrollmentMetrics, LessonEnrollmentMetrics.userId, 'userId'),
  ref('continue_lesson', ContinueLesson, ContinueLesson.userId, 'userId'),
  ref('leaderboard_entry', LeaderboardEntry, LeaderboardEntry.userId, 'userId'),
  ref('top_achievers', TopAchievers, TopAchievers.userId, 'userId'),
]

type Counts = Record<string, number>

function bump(counts: Counts, table: string, n: number) {
  counts[table] = (counts[table] ?? 0) + n
}

/** Rows touched by a statement issued without `.returning()`. Both drivers report it as `rowCount`. */
function affected(result: { rowCount: number | null }): number {
  return result.rowCount ?? 0
}

async function countRows(db: Queryable, target: PgTable, where: SQL | undefined): Promise<number> {
  const [row] = await db.select({ value: count() }).from(target).where(where)
  return row?.value ?? 0
}

async function repoint(tx: DatabaseTransaction, column: UserColumnRef, from: string, to: string): Promise<number> {
  return affected(
    await tx
      .update(column.target)
      .set({ [column.key]: to })
      .where(eq(column.column, from)),
  )
}

/**
 * `EXISTS (...)` predicates, one per table with a user-scoped natural key: true when the survivor
 * already holds a row with the same key as the outer row. Each guarded move is `UPDATE ... WHERE NOT
 * <predicate>` and its leftover delete is `DELETE ... WHERE <predicate>`, so the two statements cannot
 * disagree about which rows collided — a bare `UPDATE ... SET user_id` would abort the whole
 * transaction on the first collision, and here the collisions are the expected case.
 */
function survivorHolds(db: Queryable, survivorId: string) {
  const one = sql`1`

  const m = alias(member, 'survivor_member')
  const tm = alias(teamMember, 'survivor_team_member')
  const a = alias(Achievement, 'survivor_achievement')
  const lb = alias(LoveBook, 'survivor_love_book')
  const lbc = alias(LoveBookCollection, 'survivor_love_book_collection')
  const llm = alias(LovedLessonModule, 'survivor_loved_lesson_module')
  const n = alias(Notification, 'survivor_notification')
  const csr = alias(ContentShareRecipient, 'survivor_content_share_recipient')
  const sbc = alias(SentBookCollectionToUser, 'survivor_sent_book_collection_to_user')
  const um = alias(UserMetadata, 'survivor_user_metadata')
  const mbo = alias(MyBookCollectionOrder, 'survivor_my_book_collection_order')

  return {
    // No unique on (organization_id, user_id) — a duplicate would flip `countMembershipsByUser`,
    // the only signal "keluarkan dari sekolah" has for shared-vs-sole, and leave the student on the
    // roster forever.
    member: exists(
      db
        .select({ one })
        .from(m)
        .where(and(eq(m.userId, survivorId), eq(m.organizationId, member.organizationId))),
    ),
    teamMember: exists(
      db
        .select({ one })
        .from(tm)
        .where(and(eq(tm.userId, survivorId), eq(tm.teamId, teamMember.teamId))),
    ),
    achievement: exists(
      db
        .select({ one })
        .from(a)
        .where(and(eq(a.userId, survivorId), eq(a.achievementBadgeId, Achievement.achievementBadgeId))),
    ),
    // On an `earned_xp` row: its bonus badge is one the survivor holds. Evaluated BEFORE the
    // achievement move, while "holds" still means "held before the merge".
    badgeBonusXp: exists(
      db
        .select({ one })
        .from(a)
        .where(and(eq(a.userId, survivorId), eq(a.achievementBadgeId, EarnedXp.achievementBadgeId))),
    ),
    loveBook: exists(
      db
        .select({ one })
        .from(lb)
        .where(and(eq(lb.userId, survivorId), eq(lb.bookId, LoveBook.bookId))),
    ),
    loveBookCollection: exists(
      db
        .select({ one })
        .from(lbc)
        .where(and(eq(lbc.userId, survivorId), eq(lbc.bookCollectionId, LoveBookCollection.bookCollectionId))),
    ),
    lovedLessonModule: exists(
      db
        .select({ one })
        .from(llm)
        .where(and(eq(llm.userId, survivorId), eq(llm.lessonModuleId, LovedLessonModule.lessonModuleId))),
    ),
    notification: exists(
      db
        .select({ one })
        .from(n)
        .where(
          and(eq(n.userId, survivorId), eq(n.eventId, Notification.eventId), eq(n.audience, Notification.audience)),
        ),
    ),
    contentShareRecipient: exists(
      db
        .select({ one })
        .from(csr)
        .where(and(eq(csr.userId, survivorId), eq(csr.shareId, ContentShareRecipient.shareId))),
    ),
    sentBookCollectionToUser: exists(
      db
        .select({ one })
        .from(sbc)
        .where(
          and(eq(sbc.userId, survivorId), eq(sbc.sentBookCollectionId, SentBookCollectionToUser.sentBookCollectionId)),
        ),
    ),
    userMetadata: exists(db.select({ one }).from(um).where(eq(um.userId, survivorId))),
    collectionOrder: exists(db.select({ one }).from(mbo).where(eq(mbo.userId, survivorId))),
  }
}

/** The columns of a love table, or of an alias of one, that the dedupe needs. */
interface LoveColumns {
  target: PgTable
  userId: PgColumn
  key: PgColumn
  calculatedMetrics: PgColumn
  calculatedForMonthlyMetrics: PgColumn
}

/** The public like counter a love table is banked into: `book.likes_count` and its two siblings. */
interface LikesCounter {
  target: PgTable
  id: PgColumn
  likesCount: PgColumn
}

function bankedScore(columns: LoveColumns): SQL {
  return sql`(${columns.calculatedMetrics}::int + ${columns.calculatedForMonthlyMetrics}::int)`
}

/**
 * The compensation `unlikeBook` and its siblings apply: a love row the total-likes cron has already
 * banked into `likesCount` takes one off the counter when it goes; an unbanked one was never added and
 * nets zero. Issued BEFORE the delete it compensates, on the same predicate, so the two cannot
 * disagree about which rows went. (user, key) is unique on every love table, so a delete scoped to one
 * user removes at most one row per key and "one off per key" is exact.
 */
async function decrementBankedLikes(
  tx: DatabaseTransaction,
  love: LoveColumns,
  counter: LikesCounter,
  going: SQL | undefined,
) {
  await tx
    .update(counter.target)
    .set({ likesCount: sql`GREATEST(${counter.likesCount} - 1, 0)` })
    .where(
      inArray(
        counter.id,
        tx
          .select({ key: love.key })
          .from(love.target)
          .where(and(going, eq(love.calculatedMetrics, true))),
      ),
    )
}

/**
 * Guarded move for `love_book`, `love_book_collection` and `loved_lesson_module`, each with a hard
 * (user, target) unique index.
 *
 * Where both accounts hold the same love one copy is deleted, and the public `likesCount` is
 * compensated for it exactly as the unlike mutations do — see `decrementBankedLikes`. Which copy goes
 * still matters for the MONTHLY counters: the monthly metrics crons are one-way accumulators over
 * `calculatedForMonthlyMetrics`, and nothing in the codebase decrements them — the unlike mutations
 * do not either — so deleting a row already banked into a month leaves that month one too high for
 * good. The copy with fewer flags banked is the one that can still go cleanly; on a tie the
 * survivor's stays.
 */
async function dedupeLove(
  tx: DatabaseTransaction,
  table: string,
  survivor: LoveColumns,
  absorbed: LoveColumns,
  counter: LikesCounter,
  survivorHoldsKey: SQL,
  from: string,
  to: string,
  moved: Counts,
  deleted: Counts,
) {
  const absorbedIsMoreBanked = exists(
    tx
      .select({ one: sql`1` })
      .from(absorbed.target)
      .where(
        and(
          eq(absorbed.userId, from),
          eq(absorbed.key, survivor.key),
          sql`${bankedScore(absorbed)} > ${bankedScore(survivor)}`,
        ),
      ),
  )
  const survivorCopyGoes = and(eq(survivor.userId, to), absorbedIsMoreBanked)
  await decrementBankedLikes(tx, survivor, counter, survivorCopyGoes)
  bump(deleted, table, affected(await tx.delete(survivor.target).where(survivorCopyGoes)))
  bump(
    moved,
    table,
    affected(
      await tx
        .update(survivor.target)
        .set({ userId: to })
        .where(and(eq(survivor.userId, from), not(survivorHoldsKey))),
    ),
  )
  const absorbedLeftoverGoes = eq(survivor.userId, from)
  await decrementBankedLikes(tx, survivor, counter, absorbedLeftoverGoes)
  bump(deleted, table, affected(await tx.delete(survivor.target).where(absorbedLeftoverGoes)))
}

interface ChildRow {
  id: string
  userType: 'child' | 'main'
  createdAt: Date
  schoolClass: string | null
}

/**
 * Both rows, in id order. `lock` takes `FOR UPDATE` so two concurrent merges involving either account
 * serialise on the user rows; ordering by id keeps the lock order the same for both sides.
 */
async function loadChildren(db: Queryable, ids: string[], lock: boolean): Promise<Map<string, ChildRow>> {
  const query = db
    .select({ id: user.id, userType: user.userType, createdAt: user.createdAt, schoolClass: user.schoolClass })
    .from(user)
    .where(inArray(user.id, ids))
    .orderBy(asc(user.id))
  const rows = lock ? await query.for('update') : await query
  return new Map(rows.map((row) => [row.id, row]))
}

async function holdsMembershipIn(db: Queryable, userId: string, organizationKind: SQL): Promise<boolean> {
  const [row] = await db
    .select({ id: member.id })
    .from(member)
    .innerJoin(organization, eq(organization.id, member.organizationId))
    .where(and(eq(member.userId, userId), organizationKind))
    .limit(1)
  return row !== undefined
}

const isMemberOfFamily = (db: Queryable, userId: string) => holdsMembershipIn(db, userId, IS_FAMILY_ORG)
const isMemberOfSchool = (db: Queryable, userId: string) => holdsMembershipIn(db, userId, IS_SCHOOL_ORG)

/**
 * One reason the merge may not run, in the two registers it has to be readable in.
 *
 * The two halves exist because the same guard is consumed by two audiences. `detail` names the account
 * that tripped it and is what the previews expose — a developer reading `blockers` needs to know WHICH
 * id, and the previews never render the text. `message` is what `mergeChildAccounts` throws, and
 * `mapMergeErrors` hands that straight to the parent as the body of a `BAD_REQUEST`, so it carries no
 * id: a UUID in a parent's dialog is noise they cannot act on.
 */
interface MergeBlocker {
  /** Developer-facing, carries the offending user id. Surfaced through the previews' `blockers`. */
  detail: string
  /** Parent-facing, id-free. Thrown by the merge and shown verbatim in the dialog. */
  message: string
}

/**
 * The guards `mergeChildAccounts` enforces. Empty means the merge may proceed. Shared with the
 * previews so their `blockers` can never disagree with what the merge would throw.
 */
async function findBlockers(
  db: Queryable,
  input: MergeChildAccountsInput,
  rows: Map<string, ChildRow>,
): Promise<MergeBlocker[]> {
  const { survivorUserId, absorbedUserId } = input
  if (survivorUserId === absorbedUserId) {
    const message = 'Akun anak tidak bisa digabungkan dengan dirinya sendiri.'
    return [{ detail: `${message} (${survivorUserId})`, message }]
  }

  const blockers: MergeBlocker[] = []
  for (const id of [survivorUserId, absorbedUserId]) {
    const row = rows.get(id)
    if (!row) blockers.push({ detail: `Akun ${id} tidak ditemukan.`, message: 'Akun anak ini tidak ditemukan.' })
    else if (row.userType !== 'child') {
      blockers.push({ detail: `Akun ${id} bukan akun anak.`, message: 'Akun yang dipilih bukan akun anak.' })
    }
  }
  if (blockers.length > 0) return blockers

  if (!(await isMemberOfFamily(db, survivorUserId))) {
    blockers.push({
      detail: `Anak ${survivorUserId} belum terdaftar di akun keluarga mana pun.`,
      message: 'Anak yang dipilih belum terdaftar di akun keluarga mana pun.',
    })
  }
  // The picker greys such a child out; this is the check a hand-built tRPC call cannot skip. Step 3
  // would otherwise repoint S's school membership onto a child already on another school's roster,
  // putting one account on two rosters — and the reading tables carry no organizationId, so each
  // school would read the other's history off it.
  if (await isMemberOfSchool(db, survivorUserId)) {
    blockers.push({
      detail: `Anak ${survivorUserId} sudah terdaftar di sekolah lain.`,
      message: 'Anak yang dipilih sudah terdaftar di sekolah lain.',
    })
  }
  return blockers
}

interface SeatWindow {
  id: string
  startDate: Date
  endDate: Date
}

interface SeatPlan {
  /**
   * Every live seat of the survivor's — `endDate > now`, later-ending first. The merge never detaches
   * one, so this is also the set it keeps, and its head is how long the child stays covered.
   */
  survivorLive: SeatWindow[]
  /**
   * The subset of `survivorLive` COVERING `now` — `startDate <= now < endDate`. Non-empty is what fires
   * the seat rule: it means the family has coverage to defend today, as opposed to a prepaid renewal
   * that covers nothing yet.
   */
  survivorCurrent: SeatWindow[]
  /**
   * The absorbed account's live seats released to the SCHOOL's pool (`childId = NULL`) instead of being
   * repointed, because the survivor holds a seat covering `now`. Every live school seat is in here, not
   * only the ones whose window collides — see `planSeats` for why. Later-ending first.
   */
  absorbedLiveReleased: SeatWindow[]
  /**
   * The survivor's EXPIRED seats detached (`childId = NULL`) so a live school seat can land.
   *
   * Only ever non-empty when the survivor has no coverage today: there is nothing for the seat rule to
   * defend, and an expired row must not cost the child access it already had. Bounded by `endDate <=
   * now` in SQL rather than inferred from the branch, so a family seat granted between `planSeats`'
   * statements can never land here. Later-ending first.
   */
  survivorDetachIds: string[]
}

/**
 * Windows are compared with touching endpoints counted as overlap.
 *
 * Production enforces one live seat per child with an `EXCLUDE USING gist` constraint over
 * `(child_id, [start_date, end_date])` whose DDL lives outside this repository, so its exact bounds
 * cannot be read here. Any overlap between a seat being repointed and one the survivor already holds
 * — live or long expired — is therefore treated as a collision: the alternative is a merge that passes
 * locally and dies with a raw `23P01` in production. A seat that cannot be repointed is released to
 * its pool instead, which is what deleting S would have done to it anyway.
 */
function overlapsSurvivorSeat(db: Queryable, survivorId: string, excludeIds: string[]): SQL {
  const p = alias(Premium, 'survivor_premium')
  return exists(
    db
      .select({ one: sql`1` })
      .from(p)
      .where(
        and(
          eq(p.childId, survivorId),
          excludeIds.length > 0 ? notInArray(p.id, excludeIds) : undefined,
          lte(p.startDate, Premium.endDate),
          lte(Premium.startDate, p.endDate),
        ),
      ),
  )
}

/**
 * Decide the seat outcome without writing anything, so the preview and the merge reason identically.
 *
 * The rule is "kursi keluarga menang": a seat of the family's is never taken away. What decides it is
 * whether the family has coverage TODAY, not whether two windows happen to overlap, and the two
 * asymmetries below are the whole function.
 *
 * Keying on overlap instead looks equivalent and is not. `findActiveSeatForChild` reads "live" as
 * `endDate > now` with no lower bound, so a school seat prepaid for next term is live and need not
 * touch the family's current one. Released only on collision, such a seat would be repointed and the
 * survivor would come out holding TWO live seats — and that read orders by `endDate` DESC, so every
 * "which seat is this child on" would answer with the school's. That is school-wins by the only
 * measure the parent can see, and it re-opens the double-counted royalty the one-seat-per-child
 * invariant exists to prevent. So once the family has coverage, EVERY live seat of the school's goes
 * back to its pool, overlapping or not.
 *
 * That same missing lower bound is why the family side is tested on `startDate <= now < endDate` and
 * not on `endDate > now`. A family seat bought for next term is "live" by the loose test while
 * covering nothing today; treating it as a seat to defend would release the school seat the child is
 * actually reading on and leave them with a gap until the family window opens — destroying access the
 * merge found them holding, which no reading of "family wins" asks for.
 *
 * The other half is the survivor with no coverage today. "Family wins" cannot mean the child loses
 * premium so that a family row which expired last month keeps its provenance: there is nothing to
 * defend, so the school's seat lands, and any EXPIRED seat of the survivor's whose window touches it
 * is detached to clear the way. That detachment is what the exclusion constraint forces (see
 * `overlapsSurvivorSeat`): left attached, it would make step 4 skip the school's live seat and its
 * sweep release THAT instead, and the child would end the merge with no seat at all despite having had
 * one before it. The cost is the provenance of an unusable row; `reading_session.premiumId` keeps
 * pointing at it either way. A future-dated family seat is NOT expired and is never detached — a
 * school seat colliding with one is simply left behind for the sweep.
 *
 * `Premium` rows are never deleted — `reading_session.premiumId` and
 * `reading_session_summary.premiumId` are ON DELETE RESTRICT — so "released" means `childId = NULL`,
 * exactly what the expiry cron does, and `Premium.organizationId` still says whose pool it lands in.
 */
async function planSeats(db: Queryable, input: MergeChildAccountsInput, now: Date): Promise<SeatPlan> {
  const liveSeats = (childId: string) =>
    db
      .select({ id: Premium.id, startDate: Premium.startDate, endDate: Premium.endDate })
      .from(Premium)
      .where(and(eq(Premium.childId, childId), gt(Premium.endDate, now)))
      // Later-ending first: the same tie-break `findActiveSeatForChild` uses, so the head of
      // `absorbedLiveReleased` is the seat that read would have reported for the absorbed account.
      .orderBy(desc(Premium.endDate), asc(Premium.id))

  const [absorbedLive, survivorLive] = await Promise.all([
    liveSeats(input.absorbedUserId),
    liveSeats(input.survivorUserId),
  ])
  const survivorCurrent = survivorLive.filter((seat) => seat.startDate <= now)

  if (absorbedLive.length === 0) {
    return { survivorLive, survivorCurrent, absorbedLiveReleased: [], survivorDetachIds: [] }
  }

  if (survivorCurrent.length > 0) {
    return { survivorLive, survivorCurrent, absorbedLiveReleased: absorbedLive, survivorDetachIds: [] }
  }

  // `endDate <= now` is load-bearing, not decoration. Nothing locks `premium` — step 0 takes FOR UPDATE
  // on the two `user` rows only — and this is a third statement on its own READ COMMITTED snapshot, so
  // a family seat granted since `liveSeats` ran would be visible here. Bounded in SQL, such a row can
  // never be detached; inferred from the branch, it would be, and the merge would tear up a seat the
  // parent paid for seconds ago. It also keeps a future-dated family seat out of the list. Closed-
  // interval overlap with any of the school's live seats — the same touching-endpoints test
  // `overlapsSurvivorSeat` applies, for the same reason.
  const expiredInTheWay = await db
    .select({ id: Premium.id })
    .from(Premium)
    .where(
      and(
        eq(Premium.childId, input.survivorUserId),
        lte(Premium.endDate, now),
        or(
          ...absorbedLive.map((seat) =>
            and(lte(Premium.startDate, seat.endDate), gte(Premium.endDate, seat.startDate)),
          ),
        ),
      ),
    )
    .orderBy(desc(Premium.endDate), asc(Premium.id))

  return {
    survivorLive,
    survivorCurrent,
    absorbedLiveReleased: [],
    survivorDetachIds: expiredInTheWay.map((seat) => seat.id),
  }
}

interface SeatOutcome {
  plan: SeatPlan
  /** Seats of the absorbed account's the merge will repoint onto the survivor. */
  seatsMoving: number
  /** Every seat the absorbed account holds, moving or not. */
  absorbedSeats: number
  /** Of those, the LIVE ones that stay behind — what the owning pool actually gets back. */
  liveSeatsReleased: number
}

/**
 * The plan plus the two counts that follow from it, shared by both previews so they cannot drift.
 *
 * The exclusions reproduce the two writes step 4 performs before its repoint. The merge releases the
 * absorbed account's live seats FIRST, so by the time it repoints they no longer satisfy `childId = S`
 * — a preview reads before any of that and has to exclude them by id instead. `survivorDetachIds` goes
 * to `overlapsSurvivorSeat` for the mirror reason: those rows are still attached to the survivor here
 * and would otherwise block a school seat the merge will in fact let through.
 */
/**
 * The predicate that decides, row by row, whether a seat of the absorbed account's is repointed.
 *
 * Note the sense: TRUE means the row moves onto the survivor. It is called "stays behind" from the
 * absorbed account's point of view — the row is still there when S is deleted — which is also why its
 * negation is the set that returns to a pool.
 */
function absorbedSeatMoves(db: Queryable, input: MergeChildAccountsInput, plan: SeatPlan): SQL {
  const releasedSchoolIds = plan.absorbedLiveReleased.map((seat) => seat.id)
  const clearsSurvivor = not(overlapsSurvivorSeat(db, input.survivorUserId, plan.survivorDetachIds))
  if (releasedSchoolIds.length === 0) return clearsSurvivor

  return and(notInArray(Premium.id, releasedSchoolIds), clearsSurvivor) ?? clearsSurvivor
}

async function resolveSeatOutcome(db: Queryable, input: MergeChildAccountsInput, now: Date): Promise<SeatOutcome> {
  const { absorbedUserId: S } = input
  const plan = await planSeats(db, input, now)
  const staysBehind = absorbedSeatMoves(db, input, plan)

  const [seatsMoving, absorbedSeats, liveSeatsMoving, absorbedLiveSeats] = await Promise.all([
    countRows(db, Premium, and(eq(Premium.childId, S), staysBehind)),
    countRows(db, Premium, eq(Premium.childId, S)),
    // The live half of the same two counts. A parent is told how many seats the school gets BACK, and
    // only a live seat is something anyone gets back: an expired row that fails the overlap test is
    // detached too, but it was already worthless and no availability read will ever surface it.
    countRows(db, Premium, and(eq(Premium.childId, S), gt(Premium.endDate, now), staysBehind)),
    countRows(db, Premium, and(eq(Premium.childId, S), gt(Premium.endDate, now))),
  ])

  return { plan, seatsMoving, absorbedSeats, liveSeatsReleased: absorbedLiveSeats - liveSeatsMoving }
}

/** What the merge does to the child's premium, in the terms a parent has to agree to. */
export interface MergeSeatImpact {
  /**
   * When the child's premium coverage ends if the parent goes ahead, or null if they end up with
   * none. This is the number the confirmation screen exists to show.
   */
  coverageEndsAt: Date | null
  /**
   * When coverage STARTS, set only when the merge leaves the child with no seat covering today but one
   * that opens later — a family whose sole live seat is a prepaid renewal, whose school seat the merge
   * gives back. `coverageEndsAt` alone reads as continuous access and would hide that gap entirely.
   */
  coverageResumesAt: Date | null
  /**
   * When their coverage would have ended had the school's seat been kept — set ONLY when going ahead
   * shortens it, so a non-null value is by itself the signal that the parent is giving something up.
   */
  coverageWithSchoolSeatEndsAt: Date | null
  /**
   * LIVE seats of the school's handed back to their pool — what someone actually gets back. Expired
   * rows of the absorbed account's are detached too and deliberately not counted here: nothing can be
   * reassigned from them and saying "2 seats returned" for one usable seat is simply untrue.
   */
  releasedSchoolSeatCount: number
}

export interface MergeImpact {
  /** Exactly the guards `mergeChildAccounts` throws on. Non-empty means the merge is not offerable. */
  blockers: string[]
  seats: MergeSeatImpact
}

/**
 * Blockers and the seat outcome, and nothing else — the answer a parent's confirmation dialog needs.
 *
 * Deliberately not `previewChildMerge`: that walks every learner table with a COUNT apiece, which is
 * the right shape for an operator about to run an irreversible script and far too slow for a query
 * that refires each time the parent taps a different child. Both read the same `resolveSeatOutcome`,
 * so the dialog and the operator preview can never disagree about the seat.
 *
 * The strings in `blockers` are the guards' `detail` half — they name the offending user id and are
 * meant for a developer; a client should treat a non-empty array as "not this child" rather than
 * render it. The text a parent sees comes from the merge's own throw, not from here.
 */
export async function previewChildMergeImpact(
  db: Database,
  input: MergeChildAccountsInput,
  now: Date = new Date(),
): Promise<MergeImpact> {
  const none: MergeSeatImpact = {
    coverageEndsAt: null,
    coverageResumesAt: null,
    coverageWithSchoolSeatEndsAt: null,
    releasedSchoolSeatCount: 0,
  }

  const rows = await loadChildren(db, [input.survivorUserId, input.absorbedUserId], false)
  const blockers = await findBlockers(db, input, rows)
  if (blockers.length > 0) return { blockers: blockers.map((blocker) => blocker.detail), seats: none }

  const { plan, liveSeatsReleased } = await resolveSeatOutcome(db, input, now)

  // Both halves of the school's live seats: the later-ending one that lands on the survivor, and the
  // later-ending one that does not. Asking the database rather than reading `absorbedLiveReleased` is
  // what makes the second half right in BOTH branches — when the seat rule does not fire, a school
  // seat can still be lost by colliding with a survivor seat the merge keeps, and that loss is
  // invisible to the plan.
  const [landingSeat, lostSeat] = await Promise.all([
    findAbsorbedLiveSeat(db, input, plan, now, true),
    findAbsorbedLiveSeat(db, input, plan, now, false),
  ])

  // How long the child stays covered: the LATEST-ending seat they come out holding. `survivorLive`,
  // not `survivorCurrent` — the merge detaches no live seat of the survivor's, so a prepaid renewal
  // survives and is the real answer. Reading the trigger set here instead was wrong in exactly the
  // case it matters: a family holding a current seat plus a renewal would be told their coverage ends
  // on the current one and that the school's longer seat is being taken away, when the renewal in fact
  // outlasts both.
  const keptSeats = [...plan.survivorLive, ...(landingSeat ? [landingSeat] : [])]
  const coverageEndsAt = latest(keptSeats.map((seat) => seat.endDate))

  // "Ends 15 April" is a lie if coverage does not start until October. A family whose only live seat is
  // a not-yet-started renewal has nothing covering today, so a school seat the merge sweeps takes the
  // child's premium away NOW — the one loss a date in the future cannot express.
  const coveredToday = plan.survivorCurrent.length > 0 || (landingSeat !== undefined && landingSeat.startDate <= now)

  return {
    blockers: [],
    seats: {
      coverageEndsAt,
      coverageResumesAt: coveredToday ? null : earliest(keptSeats.map((seat) => seat.startDate)),
      coverageWithSchoolSeatEndsAt:
        lostSeat && coverageEndsAt && lostSeat.endDate > coverageEndsAt ? lostSeat.endDate : null,
      releasedSchoolSeatCount: liveSeatsReleased,
    },
  }
}

const latest = (dates: Date[]): Date | null =>
  dates.length > 0 ? new Date(Math.max(...dates.map((date) => date.getTime()))) : null

const earliest = (dates: Date[]): Date | null =>
  dates.length > 0 ? new Date(Math.min(...dates.map((date) => date.getTime()))) : null

/**
 * The absorbed account's later-ending live seat on one side or the other of the repoint.
 *
 * `moves = true` gives the seat that lands on the survivor, `false` the best one returned to its pool.
 * Ordered exactly as `findActiveSeatForChild` orders; the caller still weighs the answer against the
 * survivor's own live seats, which the merge keeps and which can outlast either.
 */
async function findAbsorbedLiveSeat(
  db: Queryable,
  input: MergeChildAccountsInput,
  plan: SeatPlan,
  now: Date,
  moves: boolean,
): Promise<{ startDate: Date; endDate: Date } | undefined> {
  const repointed = absorbedSeatMoves(db, input, plan)

  const [seat] = await db
    .select({ startDate: Premium.startDate, endDate: Premium.endDate })
    .from(Premium)
    .where(and(eq(Premium.childId, input.absorbedUserId), gt(Premium.endDate, now), moves ? repointed : not(repointed)))
    .orderBy(desc(Premium.endDate), asc(Premium.id))
    .limit(1)

  return seat
}

/**
 * Merge S into F. Runs entirely inside the caller's transaction and deletes S as its final statement;
 * see the file header for why nothing here may run outside one.
 *
 * Guards are enforced here, not assumed of the caller: the two ids must differ, both rows must exist
 * and be `user_type = 'child'`, and the survivor must already belong to a family organization and to
 * no school. Each failure throws a plain `Error` carrying the blocker's parent-facing Indonesian
 * `message` — `mapMergeErrors` in the tRPC layer passes it through as the BAD_REQUEST body, so it is
 * deliberately the id-free half of the pair and not the `detail` the previews report.
 *
 * The caller settles the `home_connect_request` row that authorised the merge (status, parent, resolved
 * family) — this function does not know which row that is — and, after COMMIT, rebuilds the lookup
 * tables for `rebuildUserIds` with one `core / rollup-users` run (`recomputeChildLookups` in
 * `@package/api`). A whole-organization `resetOrgLookupTables` is not needed: the roll-up is keyed by
 * user, so it covers the child's rows in every organization they belong to.
 *
 * @param now the merge instant, for the live-seat cut-off and cancellation stamps; injectable for tests
 */
export async function mergeChildAccounts(
  tx: DatabaseTransaction,
  input: MergeChildAccountsInput,
  now: Date = new Date(),
): Promise<MergeChildAccountsResult> {
  const { survivorUserId: F, absorbedUserId: S } = input
  const moved: Counts = {}
  const deleted: Counts = {}

  // ---------------------------------------------------------------------------------------------
  // 0. Lock and guard. Both user rows are locked FOR UPDATE for the life of the transaction, so a
  //    second merge touching either account, or a concurrent "keluarkan dari sekolah" on S, waits.
  // ---------------------------------------------------------------------------------------------
  const rows = await loadChildren(tx, [S, F], true)
  const blockers = await findBlockers(tx, input, rows)
  const blocker = blockers[0]
  if (blocker) throw new Error(blocker.message)

  const holds = survivorHolds(tx, F)

  // ---------------------------------------------------------------------------------------------
  // 1. home_connect_request — settled before anything else touches child_user_id.
  //    Only a PENDING row on both sides collides (partial unique on child_user_id WHERE PENDING).
  //    The survivor's is the one cancelled: the absorbed account's live request is, or was until the
  //    caller flipped it, the request being accepted. Terminal rows are the audit of who consented
  //    to what and would be destroyed by S's cascade, so every row moves.
  // ---------------------------------------------------------------------------------------------
  const absorbedHasPending = exists(
    tx
      .select({ one: sql`1` })
      .from(HomeConnectRequest)
      .where(and(eq(HomeConnectRequest.childUserId, S), eq(HomeConnectRequest.status, 'PENDING'))),
  )
  bump(
    deleted,
    'home_connect_request',
    affected(
      await tx
        .update(HomeConnectRequest)
        .set({ status: 'CANCELLED', cancelledAt: now })
        .where(
          and(eq(HomeConnectRequest.childUserId, F), eq(HomeConnectRequest.status, 'PENDING'), absorbedHasPending),
        ),
    ),
  )
  bump(
    moved,
    'home_connect_request',
    await repoint(
      tx,
      ref('home_connect_request', HomeConnectRequest, HomeConnectRequest.childUserId, 'childUserId'),
      S,
      F,
    ),
  )

  // ---------------------------------------------------------------------------------------------
  // 2. The survivor's own row. `createdAt` is a hard floor in the `compute<Metric>` formulas (`gte(x,
  //    user.createdAt)`): without the backdate every chart silently truncates S's history, with the
  //    source rows still sitting in the table looking fine. `schoolClass` is the grade level the
  //    school owns and prints on the login card; family-created children almost always have it null.
  //    Everything else — name, nickname, image, gender, birth date, PIN — is already F's.
  // ---------------------------------------------------------------------------------------------
  await tx
    .update(user)
    .set({
      createdAt: sql`LEAST(${user.createdAt}, (SELECT s.created_at FROM ${user} s WHERE s.id = ${S}))`,
      schoolClass: sql`COALESCE(${user.schoolClass}, (SELECT s.school_class FROM ${user} s WHERE s.id = ${S}))`,
    })
    .where(eq(user.id, F))

  // ---------------------------------------------------------------------------------------------
  // 3. member and team_member, written directly. Better Auth's API cannot join a Drizzle transaction,
  //    so this is the only atomic option; the caller fires the skipped organization-sync hooks after
  //    COMMIT. Repointing rather than inserting preserves `member.createdAt` — the date the school
  //    enrolled the student, which the roster reads — where an insert would stamp the merge date.
  // ---------------------------------------------------------------------------------------------
  bump(
    moved,
    'member',
    affected(
      await tx
        .update(member)
        .set({ userId: F })
        .where(and(eq(member.userId, S), not(holds.member))),
    ),
  )
  bump(deleted, 'member', affected(await tx.delete(member).where(eq(member.userId, S))))

  bump(
    moved,
    'team_member',
    affected(
      await tx
        .update(teamMember)
        .set({ userId: F })
        .where(and(eq(teamMember.userId, S), not(holds.teamMember))),
    ),
  )
  bump(deleted, 'team_member', affected(await tx.delete(teamMember).where(eq(teamMember.userId, S))))

  // ---------------------------------------------------------------------------------------------
  // 4. Seats. "Kursi keluarga menang" — F's live seats are never touched; see `planSeats` for why the
  //    rule turns on liveness rather than on overlap. Both detachments run before the repoint, so the
  //    repoint's `overlapsSurvivorSeat` sees the settled picture and two live seats never coexist on F
  //    even for one statement:
  //      - F has coverage today: every live seat of S's goes back to the SCHOOL's pool, so only S's
  //        expired rows are left to move, and only the ones that collide with nothing of F's.
  //      - F has none: F's expired seats blocking a live school seat are detached instead, and the
  //        school seat lands — the child keeps the access it walked in with.
  //    Each release is keyed by id off the plan, so the row subtracted below is a member of the counted
  //    set by construction and the count cannot go negative. `releasedSchoolPremiumId` names the live
  //    SCHOOL seat the rule freed, never a seat of F's, which the rule never frees.
  // ---------------------------------------------------------------------------------------------
  const seats = await planSeats(tx, input, now)
  const releasedSchoolPremiumId = seats.absorbedLiveReleased[0]?.id ?? null
  if (seats.survivorDetachIds.length > 0) {
    bump(
      deleted,
      'premium',
      affected(await tx.update(Premium).set({ childId: null }).where(inArray(Premium.id, seats.survivorDetachIds))),
    )
  }
  if (seats.absorbedLiveReleased.length > 0) {
    const released = affected(
      await tx
        .update(Premium)
        .set({ childId: null })
        .where(
          inArray(
            Premium.id,
            seats.absorbedLiveReleased.map((seat) => seat.id),
          ),
        ),
    )
    bump(deleted, 'premium', released - (releasedSchoolPremiumId === null ? 0 : 1))
  }
  bump(
    moved,
    'premium',
    affected(
      await tx
        .update(Premium)
        .set({ childId: F })
        .where(and(eq(Premium.childId, S), not(overlapsSurvivorSeat(tx, F, [])))),
    ),
  )
  bump(deleted, 'premium', affected(await tx.update(Premium).set({ childId: null }).where(eq(Premium.childId, S))))

  // ---------------------------------------------------------------------------------------------
  // 5. Achievements. Unique on (achievement_badge_id, user_id). For a badge both accounts earned,
  //    F's row survives with the EARLIER claim, and S's bonus `earned_xp` row for that badge is deleted
  //    — there is no unique on earned_xp(user_id, achievement_badge_id) to stop a second bonus row.
  //    The bonus delete MUST precede both the achievement move (after it, F holds every badge and the
  //    delete would take all of S's bonus XP) and the rebuild after COMMIT (once the doubled XP is
  //    recomputed into earned_xp_daily and lesson_metrics_daily.badgeXp it is indistinguishable from
  //    real data).
  // ---------------------------------------------------------------------------------------------
  bump(deleted, 'earned_xp', affected(await tx.delete(EarnedXp).where(and(eq(EarnedXp.userId, S), holds.badgeBonusXp))))
  await tx.execute(sql`
    UPDATE ${Achievement} AS f
    SET claimed_at = LEAST(f.claimed_at, s.claimed_at),
        created_at = LEAST(f.created_at, s.created_at)
    FROM ${Achievement} AS s
    WHERE f.user_id = ${F}
      AND s.user_id = ${S}
      AND s.achievement_badge_id = f.achievement_badge_id
  `)
  bump(
    moved,
    'achievement',
    affected(
      await tx
        .update(Achievement)
        .set({ userId: F })
        .where(and(eq(Achievement.userId, S), not(holds.achievement))),
    ),
  )
  bump(deleted, 'achievement', affected(await tx.delete(Achievement).where(eq(Achievement.userId, S))))

  // ---------------------------------------------------------------------------------------------
  // 6. The three love tables — see `dedupeLove`.
  // ---------------------------------------------------------------------------------------------
  const absorbedLoveBook = alias(LoveBook, 'absorbed_love')
  await dedupeLove(
    tx,
    'love_book',
    {
      target: LoveBook,
      userId: LoveBook.userId,
      key: LoveBook.bookId,
      calculatedMetrics: LoveBook.calculatedMetrics,
      calculatedForMonthlyMetrics: LoveBook.calculatedForMonthlyMetrics,
    },
    {
      target: absorbedLoveBook,
      userId: absorbedLoveBook.userId,
      key: absorbedLoveBook.bookId,
      calculatedMetrics: absorbedLoveBook.calculatedMetrics,
      calculatedForMonthlyMetrics: absorbedLoveBook.calculatedForMonthlyMetrics,
    },
    { target: Book, id: Book.id, likesCount: Book.likesCount },
    holds.loveBook,
    S,
    F,
    moved,
    deleted,
  )
  const absorbedLoveBookCollection = alias(LoveBookCollection, 'absorbed_love')
  await dedupeLove(
    tx,
    'love_book_collection',
    {
      target: LoveBookCollection,
      userId: LoveBookCollection.userId,
      key: LoveBookCollection.bookCollectionId,
      calculatedMetrics: LoveBookCollection.calculatedMetrics,
      calculatedForMonthlyMetrics: LoveBookCollection.calculatedForMonthlyMetrics,
    },
    {
      target: absorbedLoveBookCollection,
      userId: absorbedLoveBookCollection.userId,
      key: absorbedLoveBookCollection.bookCollectionId,
      calculatedMetrics: absorbedLoveBookCollection.calculatedMetrics,
      calculatedForMonthlyMetrics: absorbedLoveBookCollection.calculatedForMonthlyMetrics,
    },
    { target: BookCollection, id: BookCollection.id, likesCount: BookCollection.likesCount },
    holds.loveBookCollection,
    S,
    F,
    moved,
    deleted,
  )
  const absorbedLovedLessonModule = alias(LovedLessonModule, 'absorbed_love')
  await dedupeLove(
    tx,
    'loved_lesson_module',
    {
      target: LovedLessonModule,
      userId: LovedLessonModule.userId,
      key: LovedLessonModule.lessonModuleId,
      calculatedMetrics: LovedLessonModule.calculatedMetrics,
      calculatedForMonthlyMetrics: LovedLessonModule.calculatedForMonthlyMetrics,
    },
    {
      target: absorbedLovedLessonModule,
      userId: absorbedLovedLessonModule.userId,
      key: absorbedLovedLessonModule.lessonModuleId,
      calculatedMetrics: absorbedLovedLessonModule.calculatedMetrics,
      calculatedForMonthlyMetrics: absorbedLovedLessonModule.calculatedForMonthlyMetrics,
    },
    { target: LessonModule, id: LessonModule.id, likesCount: LessonModule.likesCount },
    holds.lovedLessonModule,
    S,
    F,
    moved,
    deleted,
  )

  // ---------------------------------------------------------------------------------------------
  // 7. Notifications and shares. `notification` is unique on (event_id, user_id, audience): where both
  //    accounts received the same event, F's row survives with the EARLIER read/seen/archived stamps
  //    so a read message never turns unread, and the event's counters — stamped at emit, bumped per
  //    read — drop by the duplicate they no longer describe. The counter fix runs first, while it is
  //    still visible whether BOTH rows had been read. `notification_delivery` cascades off the deleted
  //    row. `content_share_recipient` is unique on (share_id, user_id); a revocation on either side
  //    stays revoked — the teacher meant this student. `notification_preference` is dropped (F's win).
  // ---------------------------------------------------------------------------------------------
  await tx.execute(sql`
    UPDATE ${NotificationEvent} AS e
    SET recipient_count = GREATEST(0, e.recipient_count - 1),
        read_count = GREATEST(
          0,
          e.read_count - CASE WHEN d.read_at IS NOT NULL AND f.read_at IS NOT NULL THEN 1 ELSE 0 END
        )
    FROM ${Notification} AS d
    JOIN ${Notification} AS f
      ON f.event_id = d.event_id AND f.audience = d.audience AND f.user_id = ${F}
    WHERE e.id = d.event_id
      AND d.user_id = ${S}
      AND d.audience = 'RECIPIENT'
  `)
  await tx.execute(sql`
    UPDATE ${Notification} AS f
    SET read_at = LEAST(f.read_at, s.read_at),
        seen_at = LEAST(f.seen_at, s.seen_at),
        archived_at = LEAST(f.archived_at, s.archived_at)
    FROM ${Notification} AS s
    WHERE f.user_id = ${F}
      AND s.user_id = ${S}
      AND s.event_id = f.event_id
      AND s.audience = f.audience
  `)
  bump(
    moved,
    'notification',
    affected(
      await tx
        .update(Notification)
        .set({ userId: F })
        .where(and(eq(Notification.userId, S), not(holds.notification))),
    ),
  )
  bump(deleted, 'notification', affected(await tx.delete(Notification).where(eq(Notification.userId, S))))

  await tx.execute(sql`
    UPDATE ${ContentShareRecipient} AS f
    SET revoked_at = COALESCE(f.revoked_at, s.revoked_at)
    FROM ${ContentShareRecipient} AS s
    WHERE f.user_id = ${F}
      AND s.user_id = ${S}
      AND s.share_id = f.share_id
  `)
  bump(
    moved,
    'content_share_recipient',
    affected(
      await tx
        .update(ContentShareRecipient)
        .set({ userId: F })
        .where(and(eq(ContentShareRecipient.userId, S), not(holds.contentShareRecipient))),
    ),
  )
  bump(
    deleted,
    'content_share_recipient',
    affected(await tx.delete(ContentShareRecipient).where(eq(ContentShareRecipient.userId, S))),
  )

  // No unique on (sent_book_collection_id, user_id), so nothing would throw — a duplicate simply
  // renders the same shared collection twice. Same guarded move; the earlier read stamp survives.
  await tx.execute(sql`
    UPDATE ${SentBookCollectionToUser} AS f
    SET read_at = LEAST(f.read_at, s.read_at)
    FROM ${SentBookCollectionToUser} AS s
    WHERE f.user_id = ${F}
      AND s.user_id = ${S}
      AND s.sent_book_collection_id = f.sent_book_collection_id
  `)
  bump(
    moved,
    'sent_book_collection_to_user',
    affected(
      await tx
        .update(SentBookCollectionToUser)
        .set({ userId: F })
        .where(and(eq(SentBookCollectionToUser.userId, S), not(holds.sentBookCollectionToUser))),
    ),
  )
  bump(
    deleted,
    'sent_book_collection_to_user',
    affected(await tx.delete(SentBookCollectionToUser).where(eq(SentBookCollectionToUser.userId, S))),
  )

  // ---------------------------------------------------------------------------------------------
  // 8. Per-user singletons with no unique to enforce them. Two rows make an unordered `findFirst`
  //    nondeterministic — the shelf order flips between refreshes and a drag-reorder writes into the
  //    row the next read ignores. S's row fills the gap only when F has none, and at most ONE of them
  //    — the most recently updated — because nothing stopped S from accumulating duplicates of its
  //    own (the local data already has a child with two). The rest are dropped.
  // ---------------------------------------------------------------------------------------------
  const newestAbsorbedRow = (table: PgTable) =>
    sql`(SELECT id FROM ${table} WHERE user_id = ${S} ORDER BY COALESCE(updated_at, created_at) DESC LIMIT 1)`
  bump(
    moved,
    'user_metadata',
    affected(
      await tx
        .update(UserMetadata)
        .set({ userId: F })
        .where(and(eq(UserMetadata.id, newestAbsorbedRow(UserMetadata)), not(holds.userMetadata))),
    ),
  )
  bump(deleted, 'user_metadata', affected(await tx.delete(UserMetadata).where(eq(UserMetadata.userId, S))))

  bump(
    moved,
    'my_book_collection_order',
    affected(
      await tx
        .update(MyBookCollectionOrder)
        .set({ userId: F })
        .where(and(eq(MyBookCollectionOrder.id, newestAbsorbedRow(MyBookCollectionOrder)), not(holds.collectionOrder))),
    ),
  )
  bump(
    deleted,
    'my_book_collection_order',
    affected(await tx.delete(MyBookCollectionOrder).where(eq(MyBookCollectionOrder.userId, S))),
  )

  // ---------------------------------------------------------------------------------------------
  // 9. Rows that are dropped outright — see `DROPPED_COLUMNS` for the reasoning per table.
  // ---------------------------------------------------------------------------------------------
  for (const column of DROPPED_COLUMNS) {
    bump(deleted, column.table, affected(await tx.delete(column.target).where(eq(column.column, S))))
  }

  // ---------------------------------------------------------------------------------------------
  // 10. Everything that cannot collide. `organization_id` on learn_progress, learn_enrollment,
  //     course, sent_book_collection and content_share is deliberately untouched: those rows stay the
  //     school's, and rewriting them to the family would delete the school's reporting on the student.
  // ---------------------------------------------------------------------------------------------
  for (const column of [...LEARNER_COLUMNS, ...ADULT_COLUMNS]) {
    bump(moved, column.table, await repoint(tx, column, S, F))
  }

  // ---------------------------------------------------------------------------------------------
  // 11. Flag resets, scoped BY USER. The daily `calculatedFor*` flags are dead — the daily tables are
  //     recomputed from source by `compute<Metric>` and nothing reads the flags — so resetting them is
  //     harmless but no longer what rebuilds anything. What still matters is the bump: issued through
  //     Drizzle, `$onUpdateFn` moves `learn_enrollment.updatedAt` past `lesson_enrollment_metrics`'
  //     `calculatedAt`, which is the stale guard that producer and its live overlay recompute on.
  //     `calculatedForRoyalty`, `calculatedForBookMetrics` and `calculatedForBookMetricsMonthly` are
  //     never reset: those crons are one-way accumulators, and a royalty reset pays the author twice.
  // ---------------------------------------------------------------------------------------------
  await tx
    .update(ReadingSession)
    .set({
      calculatedForReadingJournal: false,
      readingJournalCalculatedAt: null,
      calculatedForReadingMetricsDaily: false,
      readingMetricsDailyCalculatedAt: null,
      calculatedForReadingHistoryDaily: false,
      readingHistoryDailyCalculatedAt: null,
      calculatedForContinueReading: false,
      continueReadingCalculatedAt: null,
    })
    .where(eq(ReadingSession.userId, F))
  await tx
    .update(EarnedXp)
    .set({
      calculatedForEarnedXpDaily: false,
      earnedXpDailyCalculatedAt: null,
      calculatedForLessonMetricsDaily: false,
      lessonMetricsDailyCalculatedAt: null,
    })
    .where(eq(EarnedXp.userId, F))
  await tx
    .update(LearnProgress)
    .set({
      calculatedForLessonMetricsDaily: false,
      lessonMetricsDailyCalculatedAt: null,
      calculatedForSubjectMetricsDaily: false,
      subjectMetricsDailyCalculatedAt: null,
      calculatedForContinueLesson: false,
      continueLessonCalculatedAt: null,
    })
    .where(eq(LearnProgress.userId, F))
  await tx
    .update(LearnEnrollment)
    .set({
      calculatedForLessonMetricsDaily: false,
      lessonMetricsDailyCalculatedAt: null,
      calculatedForLessonHistoryDaily: false,
      lessonHistoryDailyCalculatedAt: null,
      calculatedForLeaderboard: false,
      leaderboardCalculatedAt: null,
    })
    .where(eq(LearnEnrollment.userId, F))

  // ---------------------------------------------------------------------------------------------
  // 12. Derived rows for BOTH ids. S's would cascade; deleting them explicitly keeps the statement
  //     list auditable, and deleting F's is what makes the rebuild land on a clean slate.
  // ---------------------------------------------------------------------------------------------
  for (const column of DERIVED_COLUMNS) {
    bump(deleted, column.table, affected(await tx.delete(column.target).where(inArray(column.column, [S, F]))))
  }

  // ---------------------------------------------------------------------------------------------
  // 13. Delete S. Last statement, and only here: anything not yet repointed is destroyed by the
  //     cascade rather than orphaned. This also frees S's PIN back into the pool — the school's printed
  //     login card for this student is dead from this point, and the caller should say so.
  // ---------------------------------------------------------------------------------------------
  bump(deleted, 'user', affected(await tx.delete(user).where(eq(user.id, S))))

  const organizations = await tx
    .selectDistinct({ organizationId: member.organizationId })
    .from(member)
    .where(eq(member.userId, F))

  return {
    survivorUserId: F,
    absorbedUserId: S,
    movedRowCounts: moved,
    deletedRowCounts: deleted,
    releasedSchoolPremiumId,
    organizationIds: organizations.map((row) => row.organizationId),
    rebuildUserIds: [F],
  }
}

/**
 * Dry run: what `mergeChildAccounts` would touch, without writing anything. Modelled on
 * `getTeacherRemovalImpact` — count first, let a human confirm, then recount server-side inside the
 * real transaction because this answer can be stale by the time they click.
 *
 * `rowCounts` are rows that would MOVE; duplicates that would be dropped, seats that would be released
 * and rows that are dropped outright are described in `warnings`. `blockers` are exactly the guards the
 * merge itself throws on, in their developer-facing `detail` form.
 */
export async function previewChildMerge(
  db: Database,
  input: MergeChildAccountsInput,
  now: Date = new Date(),
): Promise<MergePreview> {
  const { survivorUserId: F, absorbedUserId: S } = input
  const preview: MergePreview = { survivorUserId: F, absorbedUserId: S, rowCounts: {}, warnings: [], blockers: [] }

  const rows = await loadChildren(db, [S, F], false)
  preview.blockers = (await findBlockers(db, input, rows)).map((blocker) => blocker.detail)
  const survivor = rows.get(F)
  const absorbed = rows.get(S)
  if (!survivor || !absorbed) return preview

  const holds = survivorHolds(db, F)
  const absorbedRows = (column: UserColumnRef, extra?: SQL) =>
    countRows(db, column.target, and(eq(column.column, S), extra))
  const warn = (message: string) => preview.warnings.push(message)

  // Rows that move unconditionally, keyed by table so a two-column table sums.
  for (const column of LEARNER_COLUMNS) {
    bump(preview.rowCounts, column.table, await absorbedRows(column))
  }
  for (const column of ADULT_COLUMNS) {
    const stray = await absorbedRows(column)
    bump(preview.rowCounts, column.table, stray)
    if (stray > 0) {
      warn(
        `${stray} ${column.table}.${column.key} row(s) name the absorbed child in an adult-only column; they move rather than cascade`,
      )
    }
  }
  bump(
    preview.rowCounts,
    'home_connect_request',
    await countRows(db, HomeConnectRequest, eq(HomeConnectRequest.childUserId, S)),
  )

  // Guarded moves: total minus collisions moves; collisions are dropped or merged into F's row. A
  // singleton table moves at most one row, and none when the survivor already has its own.
  const guarded: { column: UserColumnRef; holds: SQL; describe: (n: number) => string; singleton?: boolean }[] = [
    {
      column: ref('member', member, member.userId, 'userId'),
      holds: holds.member,
      describe: (n) => `${n} organization membership(s) already held by the survivor will be deduplicated`,
    },
    {
      column: ref('team_member', teamMember, teamMember.userId, 'userId'),
      holds: holds.teamMember,
      describe: (n) => `${n} class enrolment(s) already held by the survivor will be deduplicated`,
    },
    {
      column: ref('achievement', Achievement, Achievement.userId, 'userId'),
      holds: holds.achievement,
      describe: (n) =>
        `${n} badge(s) earned by both accounts: the survivor keeps the earlier claim and the duplicate bonus XP is removed`,
    },
    {
      column: ref('love_book', LoveBook, LoveBook.userId, 'userId'),
      holds: holds.loveBook,
      describe: (n) => `${n} book(s) loved by both accounts will be deduplicated`,
    },
    {
      column: ref('love_book_collection', LoveBookCollection, LoveBookCollection.userId, 'userId'),
      holds: holds.loveBookCollection,
      describe: (n) => `${n} collection(s) loved by both accounts will be deduplicated`,
    },
    {
      column: ref('loved_lesson_module', LovedLessonModule, LovedLessonModule.userId, 'userId'),
      holds: holds.lovedLessonModule,
      describe: (n) => `${n} lesson module(s) loved by both accounts will be deduplicated`,
    },
    {
      column: ref('notification', Notification, Notification.userId, 'userId'),
      holds: holds.notification,
      describe: (n) => `${n} notification(s) delivered to both accounts will be deduplicated`,
    },
    {
      column: ref('content_share_recipient', ContentShareRecipient, ContentShareRecipient.userId, 'userId'),
      holds: holds.contentShareRecipient,
      describe: (n) => `${n} share grant(s) held by both accounts will be deduplicated`,
    },
    {
      column: ref('sent_book_collection_to_user', SentBookCollectionToUser, SentBookCollectionToUser.userId, 'userId'),
      holds: holds.sentBookCollectionToUser,
      describe: (n) => `${n} shared collection(s) sent to both accounts will be deduplicated`,
    },
    {
      column: ref('user_metadata', UserMetadata, UserMetadata.userId, 'userId'),
      holds: holds.userMetadata,
      describe: () => `the survivor's profile metadata is kept; the absorbed account's is dropped`,
      singleton: true,
    },
    {
      column: ref('my_book_collection_order', MyBookCollectionOrder, MyBookCollectionOrder.userId, 'userId'),
      holds: holds.collectionOrder,
      describe: () => `the survivor's shelf order is kept; the absorbed account's is dropped`,
      singleton: true,
    },
  ]
  for (const entry of guarded) {
    const [total, colliding] = await Promise.all([absorbedRows(entry.column), absorbedRows(entry.column, entry.holds)])
    if (entry.singleton && total > 1) {
      warn(
        `the absorbed account carries ${total} ${entry.column.table} rows; at most one moves and the rest are dropped`,
      )
    }
    bump(
      preview.rowCounts,
      entry.column.table,
      entry.singleton ? (colliding > 0 ? 0 : Math.min(total, 1)) : total - colliding,
    )
    if (colliding > 0) warn(entry.describe(colliding))
  }

  const [bonusXpToDelete, absorbedPending, survivorPending, credentials] = await Promise.all([
    countRows(db, EarnedXp, and(eq(EarnedXp.userId, S), holds.badgeBonusXp)),
    countRows(
      db,
      HomeConnectRequest,
      and(eq(HomeConnectRequest.childUserId, S), eq(HomeConnectRequest.status, 'PENDING')),
    ),
    countRows(
      db,
      HomeConnectRequest,
      and(eq(HomeConnectRequest.childUserId, F), eq(HomeConnectRequest.status, 'PENDING')),
    ),
    countRows(db, account, eq(account.userId, S)),
  ])
  if (bonusXpToDelete > 0) {
    bump(preview.rowCounts, 'earned_xp', -bonusXpToDelete)
    warn(`${bonusXpToDelete} bonus XP row(s) for badges the survivor already holds will be deleted`)
  }
  if (survivorPending > 0 && absorbedPending > 0) {
    warn(`the survivor's live home-connect request will be cancelled in favour of the absorbed account's`)
  } else if (survivorPending > 0) {
    warn(`the survivor has a live home-connect request of its own; it is left untouched`)
  }
  if (credentials > 0) {
    warn(`the absorbed account has ${credentials} credential row(s); children never should, and they will be dropped`)
  }

  // Seats.
  const { plan: seats, seatsMoving, absorbedSeats } = await resolveSeatOutcome(db, input, now)
  const releasedSchoolIds = seats.absorbedLiveReleased.map((seat) => seat.id)
  bump(preview.rowCounts, 'premium', seatsMoving)
  // `survivorLive`, matching `previewChildMergeImpact` — the merge keeps every live seat of the
  // survivor's, so a prepaid renewal is what coverage really runs to. Reading `survivorCurrent` here
  // made this warning contradict the parent's dialog for exactly the family the fix was about.
  const keptSeat = seats.survivorLive[0]
  if (seats.absorbedLiveReleased.length > 0) {
    warn(
      `the survivor keeps its family seat; the absorbed account's ${seats.absorbedLiveReleased.length} live school seat(s) (${releasedSchoolIds.join(', ')}) return to the school's pool`,
    )
    // The parent is agreeing to keep their own seat, which may be the shorter of the two. Say so with
    // both dates: this is the one way the merge can shorten premium the child already had.
    const longestSchoolSeat = seats.absorbedLiveReleased[0]
    if (keptSeat && longestSchoolSeat && longestSchoolSeat.endDate > keptSeat.endDate) {
      warn(
        `premium coverage will end ${keptSeat.endDate.toISOString()} on the family seat instead of ${longestSchoolSeat.endDate.toISOString()} on the school seat being released`,
      )
    }
  }
  if (seats.survivorDetachIds.length > 0) {
    warn(
      `the survivor has no premium coverage today, so its ${seats.survivorDetachIds.length} expired seat(s) blocking the absorbed account's live school seat will be detached and that seat takes over`,
    )
  }
  const seatsReleased = absorbedSeats - seatsMoving
  if (seatsReleased > 0) {
    warn(
      `${seatsReleased} of the absorbed account's ${absorbedSeats} seat(s) do not move and return to their pool — released by the seat rule, or overlapping the survivor's seat history`,
    )
  }

  // Rows dropped outright.
  for (const column of DROPPED_COLUMNS) {
    const n = await absorbedRows(column)
    if (n > 0) warn(`${n} ${column.table} row(s) belonging to the absorbed account will be dropped`)
  }

  // Profile fields.
  if (absorbed.createdAt < survivor.createdAt) {
    warn(
      `the survivor's createdAt is backdated from ${survivor.createdAt.toISOString()} to ${absorbed.createdAt.toISOString()} so rebuilt charts keep the absorbed history`,
    )
  }
  if (survivor.schoolClass === null && absorbed.schoolClass !== null) {
    warn(`the survivor's grade level is set to the absorbed account's (${absorbed.schoolClass})`)
  }
  warn(`the absorbed account's PIN is retired; any printed login card for this student stops working`)

  return preview
}
